import { Mail, Phone, MapPin, Send, MessageCircle } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { contactFormAPI } from "../../services/api";

const REQUIRED_ERRORS = {
  name: "Name is required",
  email: "Email is required",
  phone: "Phone number is required",
  subject: "Subject is required",
  message: "Message is required",
};

export default function ContactPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contactFormData, setContactFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleContactFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setContactFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!contactFormData.name?.trim()) {
      newErrors.name = REQUIRED_ERRORS.name;
    }
    if (!contactFormData.email?.trim()) {
      newErrors.email = REQUIRED_ERRORS.email;
    }
    if (!contactFormData.phone?.trim()) {
      newErrors.phone = REQUIRED_ERRORS.phone;
    }
    if (!contactFormData.subject?.trim()) {
      newErrors.subject = REQUIRED_ERRORS.subject;
    }
    if (!contactFormData.message?.trim()) {
      newErrors.message = REQUIRED_ERRORS.message;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }

    try {
      setIsSubmitting(true);
      await contactFormAPI.submit({
        name: contactFormData.name.trim(),
        email: contactFormData.email.trim(),
        phone_number: contactFormData.phone.trim(),
        subject: contactFormData.subject.trim(),
        message: contactFormData.message.trim(),
      });
      toast.success("Message Sent Successfully", {
        description: "Thank you for your message! We'll get back to you soon.",
      });
      setContactFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      });
      setErrors({});
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      const message =
        err?.response?.data?.message ??
        err?.message ??
        "Unable to send your message. Please try again.";
      toast.error("Submission Failed", { description: message });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative flex min-h-[40vh] items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1703669020898-428cc4765bbe?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb250YWN0JTIwY29tbXVuaWNhdGlvbiUyMG9mZmljZXxlbnwxfHx8fDE3NjUxOTU5NjN8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
            alt="Contact communication"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#7A1E1E]/70 to-[#8B2E2E]/60" />
        </div>

        <div className="relative z-10 mx-auto max-w-[1400px] px-4 text-white sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="mb-6 text-5xl lg:text-6xl">
              Contact & Registration
            </h1>
            <p className="text-2xl leading-relaxed text-gray-100">
              Get in touch with us to register your hostel, explore partnership
              opportunities, or learn more about JBF.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="bg-white bg-gradient-to-t from-white to-[#fff5e6ec] py-16">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold text-[#7A1E1E] sm:text-4xl">
              Contact Form
            </h2>
            <div className="mx-auto mb-6 h-1 w-24 bg-[#D4A017]" />
          </div>

          {/* Contact Form Card */}
          <div className="mx-auto max-w-3xl overflow-hidden rounded-xl bg-white shadow-xl sm:rounded-2xl">
            <div className="p-4 sm:p-8 lg:p-12">
              {/* Section Heading */}
              <div className="mb-6 flex items-center gap-2 sm:mb-8 sm:gap-3">
                <MessageCircle className="h-5 w-5 flex-shrink-0 text-[#7A1E1E] sm:h-6 sm:w-6" />
                <h3 className="text-lg font-semibold text-[#7A1E1E] sm:text-xl lg:text-2xl">
                  General Inquiry
                </h3>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                {/* Name */}
                <div>
                  <label
                    htmlFor="contactName"
                    className="mb-2 block text-sm font-medium text-gray-700"
                  >
                    Your Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="contactName"
                    name="name"
                    value={contactFormData.name}
                    onChange={handleContactFormChange}
                    className={`w-full rounded-lg border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#7A1E1E]/20 ${errors.name
                        ? "border-red-500 focus:border-red-500"
                        : "border-gray-300 focus:border-[#7A1E1E]"
                      }`}
                    placeholder="Enter your name"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-500">{errors.name}</p>
                  )}
                </div>

                {/* Email and Phone */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="contactEmail"
                      className="mb-2 block text-sm font-medium text-gray-700"
                    >
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="contactEmail"
                      name="email"
                      value={contactFormData.email}
                      onChange={handleContactFormChange}
                      className={`w-full rounded-lg border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#7A1E1E]/20 ${errors.email
                          ? "border-red-500 focus:border-red-500"
                          : "border-gray-300 focus:border-[#7A1E1E]"
                        }`}
                      placeholder="Enter email address"
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.email}
                      </p>
                    )}
                  </div>
                  <div>
                    <label
                      htmlFor="contactPhone"
                      className="mb-2 block text-sm font-medium text-gray-700"
                    >
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="contactPhone"
                      name="phone"
                      value={contactFormData.phone}
                      onChange={handleContactFormChange}
                      className={`w-full rounded-lg border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#7A1E1E]/20 ${errors.phone
                          ? "border-red-500 focus:border-red-500"
                          : "border-gray-300 focus:border-[#7A1E1E]"
                        }`}
                      placeholder="Enter phone number"
                    />
                    {errors.phone && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.phone}
                      </p>
                    )}
                  </div>
                </div>

                {/* Subject */}
                <div>
                  <label
                    htmlFor="contactSubject"
                    className="mb-2 block text-sm font-medium text-gray-700"
                  >
                    Subject <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="contactSubject"
                    name="subject"
                    value={contactFormData.subject}
                    onChange={handleContactFormChange}
                    className={`w-full rounded-lg border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#7A1E1E]/20 ${errors.subject
                        ? "border-red-500 focus:border-red-500"
                        : "border-gray-300 focus:border-[#7A1E1E]"
                      }`}
                    placeholder="Enter subject"
                  />
                  {errors.subject && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.subject}
                    </p>
                  )}
                </div>

                {/* Message */}
                <div>
                  <label
                    htmlFor="contactMessage"
                    className="mb-2 block text-sm font-medium text-gray-700"
                  >
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="contactMessage"
                    name="message"
                    value={contactFormData.message}
                    onChange={handleContactFormChange}
                    rows={6}
                    className={`w-full rounded-lg border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#7A1E1E]/20 ${errors.message
                        ? "border-red-500 focus:border-red-500"
                        : "border-gray-300 focus:border-[#7A1E1E]"
                      }`}
                    placeholder="Enter your message"
                  />
                  {errors.message && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.message}
                    </p>
                  )}
                </div>

                {/* Submit Button */}
                <div className="flex justify-center pt-2 sm:pt-0">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#7A1E1E] px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#5A1515] disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:px-8 sm:py-4 sm:text-base"
                  >
                    {isSubmitting ? (
                      <>
                        <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <Send className="h-4 w-4 sm:h-5 sm:w-5" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="bg-white py-16">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          <div className="mb-16 grid grid-cols-1 gap-8 md:grid-cols-3">
            {[
              {
                icon: <MapPin className="h-8 w-8" />,
                title: "Address",
                content: [
                  "B-208, (South Wing) Malad Shopping Centre,",
                  "S. V. Road, Malad (West)",
                  "Mumbai - 400064",
                ],
              },
              {
                icon: <Phone className="h-8 w-8" />,
                title: "Phone",
                content: ["+91 81690 74905"],
              },
              {
                icon: <Mail className="h-8 w-8" />,
                title: "Email",
                content: ["info@jbf.org.in"],
              },
            ].map(item => (
              <div
                key={item.title}
                className="rounded-xl bg-gradient-to-br from-[#FFF9F0] to-[#FFF5E6] p-8 text-center shadow-lg"
              >
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-[#7A1E1E] to-[#8B2E2E] text-white">
                  {item.icon}
                </div>
                <h3 className="mb-4 text-xl text-[#7A1E1E]">{item.title}</h3>
                {item.content.map(line => (
                  <p key={line} className="text-gray-700">
                    {line}
                  </p>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Map Section (Placeholder) */}
      {/* <section className="bg-gradient-to-b from-white to-[#FFF5E6] py-16">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl rounded-xl bg-[#FFF9F0] p-12 text-center">
            <MapPin className="mx-auto mb-6 h-16 w-16 text-[#7A1E1E]" />
            <h3 className="mb-6 text-3xl font-bold text-[#7A1E1E]">
              Visit Our Office
            </h3>
            <p className="mb-8 text-gray-700">
              Jain Boarding Federation, National Office
              <br />
              B-208, (South Wing) Malad Shopping Centre,
              <br />
              S. V. Road, Malad (West)
              <br />
              Mumbai - 400064, Maharashtra, India
            </p>
            <button className="rounded-lg bg-[#7A1E1E] px-8 py-3 text-white transition-all hover:bg-[#5A1515]">
              Get Directions
            </button>
          </div>
        </div>
      </section> */}

      {/* WhatsApp Helpline */}
      {/* <section className="bg-gradient-to-r from-[#7A1E1E] to-[#8B2E2E] py-16 text-white">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="mb-6 text-3xl sm:text-4xl">
            Need Immediate Assistance?
          </h2>
          <p className="mb-8 text-lg text-gray-100">
            Reach out to our WhatsApp helpline for quick responses and support.
          </p>
          <button className="mx-auto flex transform items-center gap-2 rounded-lg bg-green-500 px-8 py-4 text-white shadow-lg transition-all hover:scale-105 hover:bg-green-600">
            <MessageCircle className="h-5 w-5" />
            Chat on WhatsApp
          </button>
        </div>
      </section> */}
    </div>
  );
}
